import { useState } from 'react';

export interface ShellUser {
  name: string;
  email: string;
  /** Ex.: "Administrador". Sem login configurado vira "Acesso sem login". */
  role: string;
  /** Foto da conta Google; ausente cai nas iniciais do nome. */
  picture?: string | null;
}

export function initialsOf(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('');

  return letters.toUpperCase() || 'SC';
}

interface AvatarProps {
  name: string;
  picture?: string | null;
  size?: number;
}

/**
 * A foto de quem entrou, ou as iniciais do nome quando não há foto.
 *
 * O SICOP não tem upload de avatar: a foto é a da conta Google e chega no
 * login (ver `auth/useGoogleAuth.ts`). O endereço é servido pelo Google, então
 * pode falhar por motivos que não são nossos — conta sem foto, link vencido,
 * rede bloqueando o domínio. O `onError` existe para isso: qualquer uma dessas
 * falhas vira a inicial, e nunca o quadrado de imagem quebrada.
 *
 * `referrerPolicy` no-referrer porque o googleusercontent responde 403 a
 * requisições que chegam com Referer de outra origem.
 */
export function Avatar({ name, picture, size = 32 }: AvatarProps) {
  // Guarda o endereço que falhou, e não um booleano: assim uma foto nova (a
  // pessoa trocou na conta Google) é tentada de novo em vez de herdar a falha
  // da anterior.
  const [failed, setFailed] = useState<string | null>(null);

  if (picture && picture !== failed) {
    return (
      <img
        src={picture}
        alt=""
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onError={() => setFailed(picture)}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="bg-brand-700 flex shrink-0 items-center justify-center rounded-full font-extrabold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </div>
  );
}
